import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiFile } from 'react-icons/fi';
import DataTable from '../../../components/DataTable';
import ShareDocumentModal from './ShareDocumentModal';
import DeleteDocumentModal from './DeleteDocumentModal';
import { documentsApi } from '../../../services/api';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await documentsApi.getAll(1, 10000);
      setDocuments(res.data || res || []);
    } catch {
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // ── Open modals ────────────────────────────────────────────────────────────
  const openAddModal = () => {
    setSelectedDoc(null);
    setIsEditing(false);
    setIsShareModalOpen(true);
  };

  const openEditModal = (doc) => {
    setSelectedDoc(doc);
    setIsEditing(true);
    setIsShareModalOpen(true);
  };

  const openDeleteModal = (doc) => {
    setSelectedDoc(doc);
    setIsDeleteModalOpen(true);
  };

  // ── Save (Add / Edit) ──────────────────────────────────────────────────────
  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedDoc) {
        await documentsApi.update(selectedDoc.id, formData);
        showToast('Document updated successfully.');
      } else {
        await documentsApi.create(formData);
        showToast('Document shared successfully.');
      }
      setIsShareModalOpen(false);
      fetchDocuments();
    } catch {
      showToast('Failed to save document.', 'error');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!selectedDoc) return;
    setDeleteLoading(true);
    try {
      await documentsApi.delete(selectedDoc.id);
      showToast('Document deleted successfully.');
      setIsDeleteModalOpen(false);
      setSelectedDoc(null);
      fetchDocuments();
    } catch {
      showToast('Failed to delete document.', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      key: 'documentName',
      header: 'Document Name',
      sortable: true,
      filterable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <FiFile className="text-[#0077b5] flex-shrink-0" />
          <span className="font-medium text-slate-700">{row.documentName || '—'}</span>
        </div>
      ),
    },
    {
      key: 'documentType',
      header: 'Type',
      sortable: true,
      filterable: true,
      render: (row) => (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
          {row.documentType || '—'}
        </span>
      ),
    },
    {
      key: 'organizationName',
      header: 'Organization',
      sortable: true,
      filterable: true,
      render: (row) => <span className="text-slate-600">{row.organizationName || '—'}</span>,
    },
    {
      key: 'sharedWithUsername',
      header: 'Shared With',
      sortable: true,
      filterable: true,
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-slate-700 font-medium">{row.sharedWithUsername || row.sharedWithUserName || '—'}</span>
          {row.sharedWithUserEmail && (
            <span className="text-xs text-slate-400">{row.sharedWithUserEmail}</span>
          )}
        </div>
      ),
    },
    {
      key: 'startDate',
      header: 'Start Date',
      sortable: true,
      render: (row) =>
        row.startDate
          ? new Date(row.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : '—',
    },
    {
      key: 'endDate',
      header: 'End Date',
      sortable: true,
      render: (row) => {
        if (!row.endDate) return '—';
        const end = new Date(row.endDate);
        const isExpired = end < new Date();
        return (
          <span className={isExpired ? 'text-red-500 font-medium' : 'text-slate-600'}>
            {end.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            {isExpired && <span className="ml-1 text-xs">(Expired)</span>}
          </span>
        );
      },
    }
    // {
    //   key: 'uploadedByName',
    //   header: 'Uploaded By',
    //   sortable: true,
    //   render: (row) => <span className="text-slate-500 text-sm">{row.uploadedByName || '—'}</span>,
    // },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Documents</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage and share compliance documents with organizations and users.
          </p>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 shadow-lg text-sm font-medium transition-all ${
            toast.type === 'error'
              ? 'bg-red-500 text-white'
              : 'bg-green-500 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={documents}
        rowKey="id"
        loading={loading}
        searchPlaceholder="Search documents..."
        emptyMessage="No documents found. Click '+ Add Document' to share one."
        totalLabel="documents"
        actions={(row) => [
          {
            label: 'Edit',
            icon: FiEdit2,
            onClick: () => openEditModal(row),
          },
          {
            label: 'Delete',
            icon: FiTrash2,
            danger: true,
            onClick: () => openDeleteModal(row),
          },
        ]}
        headerActions={
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white text-sm font-medium hover:bg-[#e06a1f] transition-colors"
          >
            <FiPlus />
            Add Document
          </button>
        }
      />

      {/* Add / Edit Modal */}
      <ShareDocumentModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onSave={handleSave}
        initialData={selectedDoc}
        isEditing={isEditing}
      />

      {/* Delete Confirmation Modal */}
      <DeleteDocumentModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        document={selectedDoc}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Documents;
